// 本地 Agent(雨见浏览器内置 pi)的页面侧逻辑,侧边栏(BrowserAgentLayout)和直接打开 a.yjllq.com(ChatLayout)共用。
// - 后端判为难题时发 decision {type:'local_agent', args:{task, business_name, dir}},布局在 handleDecision 里调 startLocalAgent。
// - 用户选了自己的模型(非内置):把它交给浏览器,pi 在本地直连,不经过我们的服务器、不占额度。
// - 布局需要提供:scrollToBottom()、localAgentRerun(aiMsgIndex)(跳过分流重答这一轮)。
// 浏览器侧接口见 rainsee-pc src/preloads/ai-browser-bridge.ts。

import { store, mutations } from '@/store';
import { showToast } from '@/utils/toast';

// 与 store createDefaultAgentModel / 后端 DEFAULT_*_CHECK 一致:命中即为内置模型
const BUILT_IN_ENDPOINT = 'https://api.rainsee.cn/v1/chat/completions';
const BUILT_IN_API_KEY = 'sk-aabaaaaaaaa';

export const localAgentBridge = () =>
  (typeof window !== 'undefined' && window.rainseeBrowser && window.rainseeBrowser.localAgent) || null;

/** 当前选中的文本模型;内置模型或没选返回 null(走内置 + 额度)。解析顺序同 api/chat.js。 */
export const selectedUserModel = () => {
  const settings = store.settings || {};
  const models = Array.isArray(settings.models) ? settings.models : [];
  const active = models.find((m) => m.id === settings.activeModelId) || models[0];
  const text = models.find((m) => m.id === settings.activeTextModelId) || active;
  if (!text || !text.endpoint || !text.model) return null;
  if (text.endpoint === BUILT_IN_ENDPOINT && text.apiKey === BUILT_IN_API_KEY) return null;
  return { provider: text.provider || '', endpoint: text.endpoint, apiKey: text.apiKey || '', model: text.model, name: text.name || text.model };
};

// 「制作脚本 / 制作扩展」伙伴:只在雨见浏览器(有本地 Agent)里出现。选中后这一轮不走后端分流,
// 直接交给 pi,pi 按 rainsee-pc scripts/pi/extension-guide.md 写源码并用 browser_extension_install 装进浏览器。
export const LOCAL_AGENT_BUILDER_ROLES = [
  {
    id: 'agent_userscript', name: '脚本工匠', icon: '📜', localAgentBuilder: 'userscript',
    description: '说出需求，本地 Agent 写好用户脚本并装进浏览器', systemPrompt: ''
  },
  {
    id: 'agent_extension', name: '扩展工匠', icon: '🧩', localAgentBuilder: 'extension',
    description: '说出需求，本地 Agent 做出浏览器扩展并装好', systemPrompt: ''
  }
];

// GoTab 新标签页「共创空间」的小组件:只能从新标签页进来(要有组件的 agentId 和工作目录),不放进伙伴列表。
// pi 按 rainsee-pc scripts/pi/widget-guide.md 写 widget.html,用 browser_newtab_widget_deliver 发回新标签页预览。
export const LOCAL_AGENT_WIDGET_ROLE = {
  id: 'agent_widget', name: '组件工匠', icon: '🧱', localAgentBuilder: 'widget',
  description: '说出需求，本地 Agent 做好新标签页小组件并实时预览', systemPrompt: ''
};

const hostOf = (url) => {
  try {
    const u = new URL(url);
    return /^https?:$/.test(u.protocol) ? u.hostname.replace(/^www\./, '') : '';
  } catch (e) {
    return '';
  }
};

// 从已加入历史的这一条用户消息取附件;发送时输入框附件已被 ChatService 清空。
export const localAgentMessageImages = (message) => {
  const attachments = Array.isArray(message && message.attachments) ? message.attachments : [];
  const images = attachments.filter((att) => att && att.type === 'image' && att.url)
    .map((att) => ({ url: String(att.url), name: String(att.name || '') }));
  if (message && message.imageUrl && !images.some((image) => image.url === message.imageUrl)) {
    images.push({ url: String(message.imageUrl), name: 'Image' });
  }
  return images.filter((image, index) => images.findIndex((other) => other.url === image.url) === index);
};

const localAgentRequest = (text, message) => String(text || '').trim()
  || (localAgentMessageImages(message).length ? '请查看图片，并结合之前的对话继续处理。' : '');

// 对话快照(存到 Agent 工作目录 transcript.json):只留恢复显示需要的字段,执行记录最多 300 条
const MAX_SNAPSHOT_LOG = 300;
const snapshotMessage = (m) => {
  if (!m || !['user', 'assistant'].includes(m.role)) return null;
  const out = { role: m.role, content: typeof m.content === 'string' ? m.content : '', timestamp: m.timestamp || 0 };
  const images = localAgentMessageImages(m);
  if (images.length) {
    out.attachments = images.map((image) => ({ ...image, type: 'image' }));
    out.imageUrl = images[0].url;
  }
  if (m.localAgent) {
    const s = m.localAgent;
    out.localAgent = {
      sessionId: s.sessionId || '', task: s.task || '', business: s.business || '', workspace: s.workspace || '',
      status: s.status === 'running' || s.status === 'starting' ? 'stopped' : s.status, modelName: s.modelName || '',
      log: Array.isArray(s.log) ? s.log.slice(-MAX_SNAPSHOT_LOG) : [], startedAt: s.startedAt || 0, endedAt: s.endedAt || 0,
      error: s.error || '', errorMessage: s.errorMessage || ''
    };
  }
  return out;
};
// 恢复时只认这些字段,卡片标记为不在运行(不会再接收事件)
const restoreMessage = (m) => {
  const snap = snapshotMessage(m);
  if (!snap) return null;
  if (snap.localAgent) snap.localAgent.live = false;
  return snap;
};

// rainsee-pc session.ts recordConversation 的格式:「\n## 用户 · 2026/11/2 21:50:53\n\n内容\n」
const messagesFromConversation = (md) => {
  const out = [];
  const re = /^## (用户|Agent) · (.+)$/gm;
  const heads = [];
  let m;
  while ((m = re.exec(String(md || '')))) heads.push({ who: m[1], at: m[2], start: m.index, body: re.lastIndex });
  heads.forEach((h, i) => {
    const content = md.slice(h.body, i + 1 < heads.length ? heads[i + 1].start : md.length).trim();
    if (!content) return;
    const d = /(\d{4})\D(\d{1,2})\D(\d{1,2})\D+(\d{1,2}):(\d{2}):(\d{2})/.exec(h.at);
    const time = d ? new Date(+d[1], d[2] - 1, +d[3], +d[4], +d[5], +d[6]).getTime() : NaN;
    out.push({ role: h.who === '用户' ? 'user' : 'assistant', content, timestamp: Number.isFinite(time) ? time : Date.now() });
  });
  return out;
};

export default {
  data() {
    return {
      // 雨见浏览器内置了本地 Agent 且组件完整(顶部 Agent 标注)
      localAgentAvailable: false,
      // 「改为普通回答」时下一次请求带 skip_local_agent
      skipLocalAgentOnce: false,
      // 「继续修改」Agent 生成的扩展:{id, name, kind, source, workspace, conversation, historySent}
      localAgentResume: null,
      // 当前工作目录:AI 从问题里识别出的文件夹(授权后)、拖进来或选择的文件夹。
      // 之后的本地 Agent 任务问题里没写路径时就在这里干活;输入框和工作区面板里显示
      localAgentDir: ''
    };
  },
  created() {
    // 会话键(目录或业务名)→ {sessionId, workspace, modelKey}:本页的本地 Agent 会话,不需要响应式
    this.localAgentSessions = {};
    const bridge = localAgentBridge();
    if (bridge && typeof bridge.getStatus === 'function') {
      bridge.getStatus()
        .then((status) => {
          this.localAgentAvailable = !!(status && status.available);
          this.localAgentWorkspacesRoot = (status && status.workspacesRoot) || '';
        })
        .catch(() => { this.localAgentAvailable = false; });
    }
  },
  methods: {
    /** 请求参数:带上一次性的 skip_local_agent。布局在拼 stream options 时调用。 */
    applyLocalAgentStreamOptions(streamOpts) {
      if (this.skipLocalAgentOnce) {
        streamOpts.skip_local_agent = 1;
        this.skipLocalAgentOnce = false;
      }
      return streamOpts;
    },
    // 同一目录(或业务名)、同一模型在本页复用同一个 pi 会话,追问接着干
    async startLocalAgent(args, aiMsgIndex) {
      const msg = store.history[aiMsgIndex];
      const bridge = localAgentBridge();
      const userMsg = store.history.slice(0, aiMsgIndex).reverse().find((m) => m && m.role === 'user');
      const images = localAgentMessageImages(userMsg);
      const task = localAgentRequest(args.task, userMsg);
      // display:卡片和对话记录里显示的用户原话(task 可能带给 pi 的额外说明)
      const display = String(args.display || '').trim();
      const business = String(args.business_name || '').trim();
      if (!msg) return;
      if (!bridge || !task) {
        this.answerNormallyInsteadOfLocalAgent(aiMsgIndex);
        return;
      }
      const userModel = selectedUserModel();
      const state = {
        sessionId: '', task: display || task, business, workspace: '', status: 'starting',
        modelName: userModel ? userModel.name : '',
        log: [], startedAt: Date.now(), endedAt: 0, error: '', errorMessage: '', live: true
      };
      msg.content = '';
      this.$set(msg, 'localAgent', state);
      this.scrollToBottom();

      const fail = (code, message) => {
        state.status = 'error';
        state.error = code || 'start_failed';
        state.errorMessage = message || '';
        mutations.finalizeLastMessage();
      };
      // 用户在问题里指定了文件夹(后端提取到 args.dir)就在那里干活;其次是当前工作目录;都没有按业务名建在数据目录下
      // 制作脚本/扩展用自己的目录(ignore_current_dir)
      const dir = String(args.dir || '').trim() || (args.ignore_current_dir ? '' : this.localAgentDir);
      const modelKey = userModel ? `${userModel.endpoint}|${userModel.model}` : 'builtin';
      const sessionKey = dir ? `dir:${dir.toLowerCase()}` : `biz:${business}`;
      try {
        if (images.length && !bridge.supportsImages) {
          fail('images_unsupported', '当前浏览器版本不支持给本地 Agent 发送图片，请升级浏览器后重试。');
          return;
        }
        let session = this.localAgentSessions[sessionKey];
        // 接着上一轮(按工作目录)时,复用那个业务名开的会话,别同一目录起两个 pi
        if (!session && dir) {
          const key = Object.keys(this.localAgentSessions).find((k) => {
            const s = this.localAgentSessions[k];
            return s && s.workspace && s.workspace.toLowerCase() === dir.toLowerCase() && s.modelKey === modelKey;
          });
          if (key) session = this.localAgentSessions[key];
        }
        if (session && session.modelKey === modelKey) {
          state.sessionId = session.sessionId;
          state.workspace = session.workspace;
          const res = await bridge.prompt(session.sessionId, task, display || undefined, images);
          if (res && res.ok) return;
          if (res && res.code !== 'no_session') {
            fail(res.code, res.error);
            return;
          }
        }
        if (session) {
          // 换了模型或会话已失效:结束旧会话再开新的
          bridge.dispose(session.sessionId);
          for (const k of Object.keys(this.localAgentSessions)) {
            if (this.localAgentSessions[k] === session) delete this.localAgentSessions[k];
          }
        }
        const started = await bridge.start({
          business,
          dir,
          ...(userModel ? { model: { provider: userModel.provider, endpoint: userModel.endpoint, apiKey: userModel.apiKey, model: userModel.model } } : {})
        });
        if (!started || !started.ok) {
          fail(started && started.code, started && started.error);
          return;
        }
        session = { sessionId: started.sessionId, workspace: started.workspace, modelKey };
        this.localAgentSessions[sessionKey] = session;
        // 用户的文件夹(主进程已授权)记为当前工作目录;数据目录下按业务名建的不算
        const root = String(this.localAgentWorkspacesRoot || '').toLowerCase();
        const ws = String(started.workspace || '');
        if (dir && !args.ignore_current_dir && ws && !(root && ws.toLowerCase().startsWith(root))) this.localAgentDir = ws;
        state.sessionId = session.sessionId;
        state.workspace = session.workspace;
        const res = await bridge.prompt(session.sessionId, task, display || undefined, images);
        if (!res || !res.ok) fail(res && res.code, res && res.error);
      } catch (e) {
        fail('start_failed', String((e && e.message) || e));
      }
    },
    /** 拖进来的文件夹设为工作目录(没授权过浏览器会先弹框)。返回是否设置成功。 */
    async useDroppedLocalAgentDir(file) {
      const bridge = localAgentBridge();
      if (!bridge || typeof bridge.useDroppedDir !== 'function' || !file) return false;
      try {
        const res = await bridge.useDroppedDir(file);
        if (res && res.ok) {
          this.localAgentDir = res.dir;
          return true;
        }
      } catch (e) {
        // 取不到路径或用户拒绝
      }
      return false;
    },
    /** 输入框/工作区面板拖入文件夹(file 为 null = 拖的不是文件夹) */
    async handleAgentDirDrop(file) {
      if (!file) {
        showToast('请拖入文件夹', 'warning');
        return;
      }
      if (await this.useDroppedLocalAgentDir(file)) showToast(`工作目录：${this.localAgentDir}`, 'success');
    },
    /** 系统选择框选工作目录 */
    async pickLocalAgentDir() {
      const bridge = localAgentBridge();
      if (!bridge || typeof bridge.pickDir !== 'function') return;
      try {
        const dir = await bridge.pickDir();
        if (dir) this.localAgentDir = dir;
      } catch (e) {
        // 忽略
      }
    },
    openLocalAgentDir() {
      const bridge = localAgentBridge();
      if (this.localAgentDir && bridge && typeof bridge.openPath === 'function') bridge.openPath(this.localAgentDir).catch(() => {});
    },
    clearLocalAgentDir() {
      this.localAgentDir = '';
    },
    /** 点「制作脚本 / 制作扩展」:切到对应伙伴,输入框填好开头,用户接着写需求。 */
    startLocalAgentBuilder(kind, pageUrl) {
      const role = LOCAL_AGENT_BUILDER_ROLES.find((r) => r.localAgentBuilder === kind);
      if (!role) return;
      const host = hostOf(pageUrl);
      const lead = kind === 'userscript'
        ? `帮我做一个用户脚本${host ? `，在 ${host} 上` : ''}，实现：`
        : '帮我做一个浏览器扩展，实现：';
      // 从欢迎区新做一个:不沿用「继续修改」的上下文
      this.localAgentResume = null;
      mutations.setActiveCompanions([role.id]);
      this.input = `@${role.name} ${lead}`;
      this.$nextTick(() => {
        const box = this.$refs.chatInputBox;
        if (box && box.focusTextarea) box.focusTextarea();
      });
    },
    /** 当前伙伴是制作脚本/扩展时,这一轮直接交给本地 Agent。返回 true 表示已处理。 */
    runLocalAgentBuilder(text, companion, pageUrl, userMsgIndex = store.history.length - 1) {
      const kind = companion && companion.localAgentBuilder;
      if (this.skipLocalAgentOnce || !kind || !localAgentBridge()) return false;
      const request = localAgentRequest(String(text || '').split(`@${companion.name}`).join(' '), store.history[userMsgIndex]);
      if (!request) return false;
      const resume = this.localAgentResume && this.localAgentResume.kind === kind ? this.localAgentResume : null;
      if (kind === 'widget') {
        // 组件工作目录只能由新标签页建好(带 agentId);直接选不到这个伙伴
        if (!resume || !resume.workspace) return false;
        const widgetLines = [
          resume.hasCode
            ? `修改 GoTab 新标签页小组件「${resume.name}」,当前代码在工作目录的 widget.html(用户可能手改过，以文件为准)。用户的要求:${request}`
            : `做一个 GoTab 新标签页小组件。用户的需求:${request}`,
          '按组件规格把完整代码写进 widget.html,然后调用 browser_newtab_widget_deliver 发到新标签页预览;改完再交付一次。',
          '交付成功就结束，不要再写总结(对话里会自动显示完成)。不要在回复里推演或罗列数据，直接写进代码。不要把代码贴进对话。'
        ];
        if (!resume.historySent && resume.conversation) {
          widgetLines.push(`\n以下是之前制作它时的对话记录(Markdown):\n${resume.conversation.slice(-12000)}`);
          resume.historySent = true;
        }
        mutations.addMessage({ role: 'assistant', content: '', timestamp: Date.now() });
        this.startLocalAgent(
          { task: widgetLines.join('\n'), display: request, business_name: `组件-${resume.agentId}`, dir: resume.workspace, ignore_current_dir: true },
          store.history.length - 1
        );
        return true;
      }
      const host = hostOf(pageUrl);
      const what = kind === 'userscript' ? '用户脚本(.user.js)' : '浏览器扩展(Manifest V3 文件夹)';
      const lines = resume
        ? [
          `继续修改已经装好的${what}「${resume.name}」(扩展 id ${resume.id}),源码在 ${resume.source}。用户这次的要求:${request}`,
          '改源码后用 browser_extension_install 装同一个源路径(会原地更新),打开目标网站验证、用 browser_extension_logs 查错。',
          '最后告诉用户改了什么。'
        ]
        : [
          `用户要一个${what}。需求:${request}`,
          host ? `用户当前打开的网站是 ${host}(${pageUrl})。` : '',
          '在工作目录写源码,用 browser_extension_install 装进浏览器,打开目标网站验证效果、用 browser_extension_logs 查错,改到能用为止。',
          '最后告诉用户:它叫什么、做了什么、在哪些网站生效、源码在哪,以及可以在扩展管理页停用或删除。'
        ];
      // 新开的 pi 会话不记得之前的事:第一轮把之前的对话记录带上(只取最近一段)
      if (resume && !resume.historySent && resume.conversation) {
        lines.push(`\n以下是之前制作它时的对话记录(Markdown):\n${resume.conversation.slice(-12000)}`);
        resume.historySent = true;
      }
      const task = lines.filter(Boolean).join('\n');
      // 同一网站的脚本 / 同一类扩展复用一个工作目录和 pi 会话,追问「再改改」接着改;继续修改时回到原工作目录
      const business = kind === 'userscript' ? `脚本-${host || '通用'}` : '扩展制作';
      mutations.addMessage({ role: 'assistant', content: '', timestamp: Date.now() });
      this.startLocalAgent(
        { task, display: request, business_name: business, dir: resume ? resume.workspace : '', ignore_current_dir: true },
        store.history.length - 1
      );
      return true;
    },
    /** 扩展管理页点「继续修改」:载入之前的对话,切到对应伙伴,用户接着写要改什么。 */
    async resumeLocalAgentBuilder(extensionId) {
      const bridge = localAgentBridge();
      let ctx = null;
      try {
        ctx = bridge && typeof bridge.getBuilderContext === 'function' ? await bridge.getBuilderContext(extensionId) : null;
      } catch (e) {
        ctx = null;
      }
      if (!ctx) {
        mutations.addMessage({ role: 'assistant', content: '没有找到这个扩展的制作记录，它可能已被删除。', timestamp: Date.now() });
        mutations.finalizeLastMessage();
        return;
      }
      const role = LOCAL_AGENT_BUILDER_ROLES.find((r) => r.localAgentBuilder === ctx.kind) || LOCAL_AGENT_BUILDER_ROLES[1];
      // 页面快照优先;老记录只有 conversation.md,按「## 用户/Agent · 时间」拆回消息
      let transcript = Array.isArray(ctx.transcript) ? ctx.transcript.map(restoreMessage).filter(Boolean) : [];
      if (!transcript.length && ctx.conversation) transcript = messagesFromConversation(ctx.conversation);
      // 有页面快照:新开一个对话,原样恢复当时的消息(含执行卡片)。pi 那边按工作目录续上原会话,不用再贴历史
      this.localAgentResume = { ...ctx, transcript: null, historySent: transcript.length > 0 };
      const what = ctx.kind === 'userscript' ? '用户脚本' : '扩展';
      if (transcript.length) {
        mutations.clearHistory();
        for (const m of transcript) mutations.addMessage(m);
        mutations.addMessage({
          role: 'assistant',
          content: `已恢复制作「${ctx.name}」时的对话。说说这次要改什么，会交给原来的本地 Agent 接着改。`,
          timestamp: Date.now()
        });
      } else {
        // 老记录只有 Markdown 对话:显示在气泡里(二级标题降级,免得太大)
        const history = ctx.conversation
          ? `\n\n**之前的对话**\n\n${ctx.conversation.trim().slice(-6000).replace(/^## /gm, '#### ')}`
          : '\n\n(这个扩展没有保存对话记录，Agent 会直接读源码继续改。)';
        mutations.addMessage({
          role: 'assistant',
          content: `继续修改${what}「${ctx.name}」。源码:${ctx.source}${history}\n\n---\n说说这次要改什么。`,
          timestamp: Date.now()
        });
      }
      mutations.finalizeLastMessage();
      mutations.setActiveCompanions([role.id]);
      this.input = `@${role.name} `;
      this.$nextTick(() => {
        this.scrollToBottom(true);
        const box = this.$refs.chatInputBox;
        if (box && box.focusTextarea) box.focusTextarea();
      });
    },
    /**
     * 新标签页「共创空间」点 AI 生成 / 继续修改:载入这个组件之前的对话(有的话),切到组件工匠;
     * 新标签页带了需求就直接交给本地 Agent(同一工作目录续上原 pi 会话)。
     */
    async resumeWidgetBuilder(agentId) {
      const bridge = localAgentBridge();
      let ctx = null;
      try {
        ctx = bridge && typeof bridge.getWidgetBuilderContext === 'function' ? await bridge.getWidgetBuilderContext(agentId) : null;
      } catch (e) {
        ctx = null;
      }
      if (!ctx) {
        mutations.addMessage({ role: 'assistant', content: '没有找到这个组件的工作目录，请回到新标签页重新点「AI 生成」。', timestamp: Date.now() });
        mutations.finalizeLastMessage();
        return;
      }
      let transcript = Array.isArray(ctx.transcript) ? ctx.transcript.map(restoreMessage).filter(Boolean) : [];
      if (!transcript.length && ctx.conversation) transcript = messagesFromConversation(ctx.conversation);
      this.localAgentResume = { ...ctx, transcript: null, historySent: transcript.length > 0 };
      const role = LOCAL_AGENT_WIDGET_ROLE;
      if (transcript.length) {
        mutations.clearHistory();
        for (const m of transcript) mutations.addMessage(m);
      }
      mutations.setActiveCompanions([role.id]);
      const request = String(ctx.request || '').trim();
      if (request) {
        mutations.addMessage({ role: 'user', content: request, timestamp: Date.now() });
        this.runLocalAgentBuilder(request, role, '');
        this.input = `@${role.name} `;
        this.$nextTick(() => this.scrollToBottom(true));
        return;
      }
      mutations.addMessage({
        role: 'assistant',
        content: transcript.length
          ? `已恢复制作「${ctx.name}」时的对话。说说这次要改什么，会交给原来的本地 Agent 接着改。`
          : ctx.hasCode
            ? `继续修改组件「${ctx.name}」。说说这次要改什么。`
            : '说说你想要一个什么样的新标签页小组件，做好后会直接在新标签页里预览。',
        timestamp: Date.now()
      });
      mutations.finalizeLastMessage();
      this.input = `@${role.name} `;
      this.$nextTick(() => {
        this.scrollToBottom(true);
        const box = this.$refs.chatInputBox;
        if (box && box.focusTextarea) box.focusTextarea();
      });
    },
    onLocalAgentFinished(index) {
      if (index === store.history.length - 1) this.scrollToBottom();
      mutations.finalizeLastMessage();
      // 整段对话存到 Agent 工作目录,扩展管理页「继续修改」时原样恢复
      const msg = store.history[index];
      const bridge = localAgentBridge();
      const sessionId = msg && msg.localAgent && msg.localAgent.sessionId;
      // 组件:交付时新标签页已经收到了,这里不用 pi 的总结,只说完成没有
      const state = msg && msg.localAgent;
      if (state && state.status === 'done' && String(state.business || '').startsWith('组件-cw-')) {
        const delivered = (state.log || []).some((x) => x.kind === 'tool' && x.tool === 'browser_newtab_widget_deliver' && x.status === 'ok');
        msg.content = delivered
          ? '组件已完成，已发到新标签页预览。还要调整的话直接说。'
          : '本地 Agent 结束了，但没有把组件发到新标签页。可以说「继续」让它接着做完。';
      }
      if (sessionId && bridge && typeof bridge.saveTranscript === 'function') {
        bridge.saveTranscript(sessionId, store.history.map(snapshotMessage).filter(Boolean)).catch(() => {});
      }
    },
    /**
     * 上一轮是本地 Agent 做的(有工作目录),这一轮直接接着交给它,不再经后端判断。
     * 「改为普通回答」重发时(skipLocalAgentOnce)不走这里。返回 true 表示已处理。
     */
    continueLastLocalAgent(text, userMsgIndex = store.history.length - 1) {
      if (this.skipLocalAgentOnce || !localAgentBridge()) return false;
      const request = localAgentRequest(text, store.history[userMsgIndex]);
      if (!request) return false;
      // 刚加进去的是这条用户消息,往前找上一条助手回复
      let last = null;
      for (let i = userMsgIndex - 1; i >= 0; i -= 1) {
        const m = store.history[i];
        if (m && m.role === 'assistant' && m.localAgent && m.localAgent.workspace) {
          last = m;
          break;
        }
      }
      const state = last && last.localAgent;
      if (!state || !state.workspace) return false;
      mutations.addMessage({ role: 'assistant', content: '', timestamp: Date.now() });
      this.startLocalAgent(
        { task: request, display: request, business_name: state.business || '', dir: state.workspace },
        store.history.length - 1
      );
      return true;
    },
    /**
     * 卡片上「停止并重试」:卡片已经停掉这一轮(停不下来时浏览器会结束 Agent 进程),
     * 这里让同一工作目录的 Agent 从中断处接着做。pi 的会话记录在工作目录里,进程重开也能续上上下文。
     */
    retryStalledLocalAgent(index) {
      const msg = store.history[index];
      const state = msg && msg.localAgent;
      if (!state || !state.workspace || this.isSending) return;
      const display = '继续(上一步卡住了，已停止重试)';
      const task = '上一步执行时长时间没有进展，已被用户停止。请检查已经完成到哪一步(看工作目录里的文件)，'
        + '从中断处继续完成原来的任务。如果是某个命令或浏览器程序卡住了，换一种不会卡住的做法'
        + '(命令加 timeout、不要等待输入、不要在前台启动常驻进程)。';
      mutations.addMessage({ role: 'user', content: display, timestamp: Date.now() });
      mutations.addMessage({ role: 'assistant', content: '', timestamp: Date.now() });
      this.startLocalAgent(
        { task, display, business_name: state.business || '', dir: state.workspace, ignore_current_dir: true },
        store.history.length - 1
      );
      this.$nextTick(() => this.scrollToBottom(true));
    },
    // 用户不想交给本地 Agent:结束这轮,跳过分流重答
    answerNormallyInsteadOfLocalAgent(aiMsgIndex) {
      const msg = store.history[aiMsgIndex];
      const bridge = localAgentBridge();
      if (msg && msg.localAgent) {
        if (bridge && msg.localAgent.sessionId && msg.localAgent.status === 'running') {
          bridge.abort(msg.localAgent.sessionId);
        }
        this.$delete(msg, 'localAgent');
      }
      if (aiMsgIndex !== store.history.length - 1) return;
      this.skipLocalAgentOnce = true;
      this.localAgentRerun(aiMsgIndex);
    }
  }
};
